import React, { useState, useEffect, useCallback } from 'react'
import api from '../api/client'
import type { Shift, ShiftAssignment } from '../types'
import { X, Calendar, Clock, MapPin, Users, CheckCircle, XCircle, UserPlus, MessageSquare } from 'lucide-react'
import { useOpenConversation } from '../hooks/useOpenConversation'
import { formatDayDateToLocal, formatTimeRangeToLocal, getTimezoneAbbreviation } from '../utils/timezone'
import AssignVolunteerModal from './AssignVolunteerModal'
import ContactAdminModal from './ContactAdminModal'
import { useLocale } from '../contexts/LocaleContext'
import { useAuth } from '../contexts/AuthContext'

interface ShiftDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  shift: Shift | null
  onShiftUpdated: () => void
  userRole?: string
}

const ShiftDetailsModal: React.FC<ShiftDetailsModalProps> = ({
  isOpen,
  onClose,
  shift,
  onShiftUpdated,
  userRole
}) => {
  const { t } = useLocale()
  const { profile, isAdmin } = useAuth()
  const [assignments, setAssignments] = useState<ShiftAssignment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [isContactAdminModalOpen, setIsContactAdminModalOpen] = useState(false)
  const { open: openChat, opening, error: chatError } = useOpenConversation()
  const canOpenTeamChat =
    !!shift &&
    (isAdmin ||
      assignments.some((a) => a.volunteer_id === profile?.id && a.status !== 'cancelled'))

  const fetchAssignments = useCallback(async () => {
    if (!shift) return

    try {
      setLoading(true)
      const data = await api.shifts.getAssignments(shift.id)
      setAssignments(data || [])
    } catch (err: any) {
      setError(err.message || 'Failed to fetch assignments')
    } finally {
      setLoading(false)
    }
  }, [shift])

  useEffect(() => {
    if (isOpen && shift) {
      fetchAssignments()
    }
  }, [isOpen, shift, fetchAssignments])

  const handleUpdateAssignmentStatus = async (assignmentId: string, newStatus: string) => {
    try {
      await api.shifts.updateAssignment(assignmentId, { status: newStatus })

      const assignment = assignments.find((a: any) => a.id === assignmentId)
      if (assignment && shift) {
        if (newStatus === 'confirmed') {
          try {
            await api.notifications.send('shift_assigned', {
              volunteerEmail: assignment.email,
              volunteerFirstName: assignment.first_name,
              volunteerLastName: assignment.last_name,
              shiftTitle: shift.title,
              shiftDescription: shift.description,
              shiftStartTime: shift.start_time,
              shiftEndTime: shift.end_time,
              shiftLocation: shift.location,
              eventTitle: shift.event?.title,
              assignedAt: assignment.assigned_at,
              formattedStartTime: new Date(shift.start_time).toLocaleString('nl-NL'),
              formattedEndTime: new Date(shift.end_time).toLocaleString('nl-NL'),
              formattedAssignedDate: new Date(assignment.assigned_at).toLocaleDateString('nl-NL')
            })
          } catch (e) { console.error('Error sending shift assignment email:', e) }
        }
        if (newStatus === 'cancelled') {
          try {
            await api.notifications.send('shift_removed', {
              volunteerEmail: assignment.email,
              volunteerFirstName: assignment.first_name,
              volunteerLastName: assignment.last_name,
              shiftTitle: shift.title,
              shiftDescription: shift.description,
              shiftStartTime: shift.start_time,
              shiftEndTime: shift.end_time,
              shiftLocation: shift.location,
              eventTitle: shift.event?.title,
              removedAt: new Date().toISOString(),
              formattedStartTime: new Date(shift.start_time).toLocaleString('nl-NL'),
              formattedEndTime: new Date(shift.end_time).toLocaleString('nl-NL'),
              formattedRemovedDate: new Date().toLocaleDateString('nl-NL')
            })
          } catch (e) { console.error('Error sending shift removal email:', e) }
        }
      }

      await fetchAssignments()
      onShiftUpdated()
    } catch (err: any) {
      setError(err.message || 'Failed to update assignment')
    }
  }

  const handleVolunteerAssigned = () => {
    fetchAssignments()
    onShiftUpdated()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'bg-yellow-100 text-yellow-800'
      case 'confirmed':
        return 'bg-blue-100 text-blue-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen || !shift) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('shiftDetails.shiftDetailsTitle')} {shift.title}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {/* Shift Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">{t('shiftDetails.shiftInfo')}</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDayDateToLocal(shift.start_time, 'MMM d, yyyy')}
                      <span className="ml-1 text-xs text-gray-400">({getTimezoneAbbreviation()})</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      {formatTimeRangeToLocal(shift.start_time, shift.end_time)}
                      <span className="ml-1 text-xs text-gray-400">({getTimezoneAbbreviation()})</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      {shift.location}
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      {shift.current_volunteers}/{shift.max_volunteers} {t('shiftDetails.volunteers')}
                    </div>
                  </div>
                </div>

                {shift.description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">{t('shiftDetails.description')}</h4>
                    <p className="text-sm text-gray-600">{shift.description}</p>
                  </div>
                )}

                {shift.event && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">{t('shiftDetails.event')}</h4>
                    <p className="text-sm text-gray-600">{shift.event.title}</p>
                  </div>
                )}
              </div>

              {/* Volunteer Assignments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">{t('shiftDetails.volunteerAssignments')}</h4>
                  {userRole === 'admin' && shift.current_volunteers < shift.max_volunteers && (
                    <button
                      onClick={() => setIsAssignModalOpen(true)}
                      className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span>{t('shiftDetails.assign')}</span>
                    </button>
                  )}
                </div>
                {loading ? (
                  <div className="text-sm text-gray-500">{t('shiftDetails.loadingAssignments')}</div>
                ) : assignments.length === 0 ? (
                  <div className="text-sm text-gray-500">{t('shiftDetails.noVolunteersAssigned')}</div>
                ) : (
                  <div className="space-y-2">
                    {assignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          {assignment.avatar_url ? (
                            <img
                              className="h-6 w-6 rounded-full"
                              src={assignment.avatar_url}
                              alt={assignment.first_name || 'Volunteer'}
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-700">
                                {assignment.first_name?.[0] || 'V'}
                              </span>
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-900">
                            {assignment.first_name} {assignment.last_name}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.status)}`}>
                            {assignment.status}
                          </span>
                          {isAdmin && assignment.volunteer_id !== profile?.id && (
                            <button
                              onClick={() => openChat(() => api.messaging.openDirect(assignment.volunteer_id))}
                              className="text-gray-500 hover:text-gray-800"
                              title={t('messages.message')}
                              aria-label={t('messages.message')}
                            >
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          )}
                          {assignment.status === 'assigned' && isAdmin && (
                            <button
                              onClick={() => handleUpdateAssignmentStatus(assignment.id, 'confirmed')}
                              className="text-green-600 hover:text-green-800"
                              title={t('shiftDetails.confirm')}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          {assignment.status === 'confirmed' && userRole === 'admin' && (
                            <button
                              onClick={() => handleUpdateAssignmentStatus(assignment.id, 'completed')}
                              className="text-blue-600 hover:text-blue-800"
                              title={t('shiftDetails.markComplete')}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          {/* Admins can cancel directly, volunteers see contact modal */}
                          {(assignment.status === 'assigned' || assignment.status === 'confirmed') && (
                            isAdmin ? (
                              <button
                                onClick={() => handleUpdateAssignmentStatus(assignment.id, 'cancelled')}
                                className="text-red-600 hover:text-red-800"
                                title={t('shiftDetails.cancel')}
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            ) : assignment.volunteer_id === profile?.id ? (
                              <button
                                onClick={() => setIsContactAdminModalOpen(true)}
                                className="text-red-600 hover:text-red-800"
                                title={t('shiftDetails.cancel')}
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            ) : null
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {chatError && <p className="px-6 pb-2 text-sm text-red-700">{chatError}</p>}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
            >
              {t('shiftDetails.close')}
            </button>
            {canOpenTeamChat && (
              <button
                type="button"
                disabled={opening}
                onClick={() => openChat(() => api.messaging.forShift(shift.id))}
                className="mt-3 w-full inline-flex items-center justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 sm:mt-0 sm:w-auto sm:text-sm"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                {t('messages.teamChat')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Assign Volunteer Modal */}
      <AssignVolunteerModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        shift={shift}
        onVolunteerAssigned={handleVolunteerAssigned}
      />

      {/* Contact Admin Modal */}
      <ContactAdminModal
        isOpen={isContactAdminModalOpen}
        onClose={() => setIsContactAdminModalOpen(false)}
      />
    </div>
  )
}

export default ShiftDetailsModal
